import { ArrowDownRightIcon, ArrowRightIcon, ArrowUpRightIcon } from "@heroicons/react/24/outline";
import { getCashflowAccentColors } from "../../../../lib/accentPalette";
import { QuinnKpiTrend, QUINN_KPI_MAX_COLUMNS } from "../../types";
import { formatQuinnDeltaFromCents, formatQuinnEuroFromCents } from "../../utils/formatters.ts";

export type QuinnKpi = {
    label: string;
    value_cents?: number;
    value_text?: string;
    delta_cents?: number;
    delta_text?: string;
    trend?: QuinnKpiTrend;
};

const CASHFLOW = getCashflowAccentColors();

const TREND_ICON = {
    [QuinnKpiTrend.Up]: ArrowUpRightIcon,
    [QuinnKpiTrend.Down]: ArrowDownRightIcon,
    [QuinnKpiTrend.Flat]: ArrowRightIcon,
} as const;

const TREND_COLOR = {
    [QuinnKpiTrend.Up]: CASHFLOW.inflow.base,
    [QuinnKpiTrend.Down]: CASHFLOW.outflow.base,
    [QuinnKpiTrend.Flat]: "#6B7280",
} as const;

function resolveValue(item: QuinnKpi): string | null {
    if (typeof item.value_cents === "number" && Number.isInteger(item.value_cents)) {
        return formatQuinnEuroFromCents(item.value_cents);
    }
    if (typeof item.value_text === "string" && item.value_text.trim()) {
        return item.value_text.trim();
    }
    return null;
}

function resolveDelta(item: QuinnKpi): string | null {
    if (typeof item.delta_cents === "number" && Number.isInteger(item.delta_cents)) {
        return formatQuinnDeltaFromCents(item.delta_cents);
    }
    if (typeof item.delta_text === "string" && item.delta_text.trim()) {
        return item.delta_text.trim();
    }
    return null;
}

export function QuinnKpiBlock({ items }: { items: QuinnKpi[] }) {
    if (!items.length) return null;

    const columns = Math.min(items.length, QUINN_KPI_MAX_COLUMNS);

    return (
        <div className="my-3 grid gap-2" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
            {items.map((item, index) => {
                const value = resolveValue(item);
                if (!value) return null;

                const trend = item.trend ?? QuinnKpiTrend.Flat;
                const Icon = TREND_ICON[trend];
                const delta = resolveDelta(item);
                return (
                    <div key={`${item.label}-${index}`} className="rounded-xl border border-gray-200 bg-gray-50/70 px-3 py-2">
                        <p className="text-[10px] font-medium uppercase tracking-wider text-gray-500">{item.label}</p>
                        <p className="mt-0.5 text-base font-semibold tabular-nums tracking-tight text-gray-900">{value}</p>
                        {delta && (
                            <p
                                className="mt-0.5 flex items-center gap-1 text-[11px] font-medium"
                                style={{ color: TREND_COLOR[trend] }}
                            >
                                <Icon className="h-3 w-3" />
                                {delta}
                            </p>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
